import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatOptionModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DataType } from '@vendasta/automata';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyInfiniteScrollTriggerModule } from '@vendasta/galaxy/infinite-scroll-trigger';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { isEqual } from 'lodash-es';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { debounceTime, map, take, takeUntil } from 'rxjs/operators';
import {
  convertToStepReferenceString,
  isReferencingAPreviousStep,
} from '../../../automation-editor/components/common/utils';
import { ALL_VARIABLE_MENU_DATA_TYPES } from '../../../common/constants';
import { AutomationVariableMenuComponent } from '../automation-variable-menu/automation-variable-menu.component';
import { CapitalizePipe } from '../capitalize.pipe';
import { AutomationReplacementChipsDataService } from './interfaces';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { AutomationViewService } from '../../../automation/automation-view.service';
import { CurrentAutomationService } from '@galaxy/automata/shared';

@Component({
  selector: 'automata-automation-replacement-chips',
  templateUrl: './automation-replacement-chips.component.html',
  styleUrls: ['./automation-replacement-chips.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutomationReplacementChipsComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => AutomationReplacementChipsComponent),
      multi: true,
    },
  ],
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyFormFieldModule,
    FormsModule,
    MatChipsModule,
    MatIconModule,
    MatAutocompleteModule,
    AutomationVariableMenuComponent,
    GalaxyInfiniteScrollTriggerModule,
    MatOptionModule,
    GalaxyLoadingSpinnerModule,
    MatInputModule,
    CapitalizePipe,
    ReactiveFormsModule,
    GalaxyAlertModule,
  ],
})
export class AutomationReplacementChipsComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator {
  readonly separatorKeysCodes = [ENTER, COMMA] as const;
  @Input() label = '';
  @Input() hint = '';
  // noResultsWarning is a message that shows up if the initial load returns no results. There is no default for this so it will
  // only show up if you pass in a value.
  @Input() noResultsWarning = '';
  // noResultsFound is the message to show when there are no results found in the dropdown menu when searching
  @Input() noResultsFound = 'COMMON.NO_RESULTS_FOUND';
  @Input() placeholder = '';
  @Input() dataService: AutomationReplacementChipsDataService<any>;
  @Input() pageSize = 25;
  @Input() isRequired = false;
  @Input() disabled = false;
  // disableIfTemplate will check if we're in a template view and disable the field for you.
  // This will also override the hint text to explain why the field is disabled.
  @Input() disableIfTemplate = false;
  @Input() multiple = true;
  @Input() supportDataPassing = true;
  @Input() supportedDataTypes: DataType[] = ALL_VARIABLE_MENU_DATA_TYPES;
  // extraData is used to pass static data to the doLookup function. e.g. Used to tell the SalespersonSelectorService to not
  // add additional assignees to the list of options.
  @Input() extraData: any;
  // allowCustomValues allows users to type in values instead of selecting for the autocomplete list. This is mainly used for tags.
  @Input() allowCustomValues = false;

  @Input() bottomSpacing: 'default' | 'small' | 'none' | false = 'default';
  @Input() showLabel = true;

  @Output() finishedInitialLoading: EventEmitter<boolean> = new EventEmitter<boolean>();
  selectedOptions: FormControl<(any | string)[]>;
  searchControl = new FormControl<string>('');
  destroySubject = new Subject<void>();
  chipsDisabled = false;

  loadingMore = false;
  initialLoading = true;
  options$$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  cursor = '';
  hasMore = false;

  supportsSearching = false;
  showNoResultsWarning = false;
  showSearchNotFound = false;

  disabled$: Observable<boolean>;
  hint$: Observable<string>;

  @ViewChild('searchEntities', { static: false }) private searchEntitiesInput: ElementRef<HTMLInputElement>;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly translateService: TranslateService,
    private automationViewService: AutomationViewService,
    private readonly alertService: SnackbarService,
    private readonly currentAutomationService: CurrentAutomationService,
  ) {}

  ngOnInit(): void {
    this.supportsSearching = this.dataService.supportsSearching;

    this.searchControl.valueChanges.pipe(takeUntil(this.destroySubject), debounceTime(400)).subscribe(() => {
      this.cursor = '';
      this.options$$.next([]);
      this.doLookup(this.searchControl.value);
    });

    this.disabled$ = this.automationViewService.isTemplateView().pipe(
      map((isTemplateView) => {
        return (this.disableIfTemplate && isTemplateView) || this.disabled;
      }),
    );

    this.disabled$.pipe(take(1)).subscribe((disabled) => {
      // Don't need to do the initial lookup if the field is disabled
      if (!disabled) {
        this.doLookup('');
      }
    });

    this.hint$ = this.automationViewService.isTemplateView().pipe(
      map((isTemplateView) => {
        if (this.disableIfTemplate && isTemplateView) {
          return 'AUTOMATIONS.COMMON.DISABLED_DUE_TO_TEMPLATE';
        }
        return this.hint;
      }),
    );

    this.selectedOptions = new FormControl<(any | string)[]>([], this.isRequired ? [Validators.required] : []);
    this.automationViewService
      .isTemplateView()
      .pipe(take(1))
      .subscribe((isTemplateView) => {
        if (this.disableIfTemplate && isTemplateView) {
          // Clear the validation if the field is disabled so the form can pass validation
          this.isRequired = false;
          this.selectedOptions.clearValidators();
          this.selectedOptions.updateValueAndValidity();
        }
      });
  }

  registerOnChange(fn: any): void {
    this.selectedOptions.valueChanges
      .pipe(
        takeUntil(this.destroySubject),
        map((entities) => {
          if (this.multiple) {
            return entities.map((e) =>
              this.isEntityAPreviousStepValue(e) ? e : this.dataService.convertDataToFormValue(e),
            );
          } else {
            if (entities !== null && entities !== undefined) {
              return this.isEntityAPreviousStepValue(entities)
                ? entities
                : this.dataService.convertDataToFormValue(entities);
            }
          }
        }),
      )
      .subscribe((values) => {
        fn(values);
      });
  }

  registerOnTouched(fn: any): void {
    this.selectedOptions.valueChanges.pipe(takeUntil(this.destroySubject)).subscribe(fn);
  }

  writeValue(entities: any): void {
    if (!this.multiple) {
      if (entities !== null && entities !== undefined) {
        entities = [entities];
      } else {
        entities = [];
      }
    } else {
      // If there was a previous value that wasn't an array, and we switch to a rule that
      // is expecting an array, we need to convert it to an array
      if (!Array.isArray(entities)) {
        entities = [entities];
      }
    }
    const ids = entities.filter((e) => !this.isEntityAPreviousStepValue(e));
    const previousStepValues = entities.filter((e) => this.isEntityAPreviousStepValue(e));
    if (ids?.length == 0) {
      if (this.multiple) {
        this.selectedOptions.patchValue(previousStepValues, { emitEvent: false });
      } else {
        this.selectedOptions.patchValue(previousStepValues[0], { emitEvent: false });
      }
      this.finishedInitialLoading.emit(true);
      this.initialLoading = false;
    } else {
      this.dataService
        .getMulti(ids, this.extraData)
        .pipe(take(1))
        .subscribe((results) => {
          if (this.multiple) {
            this.selectedOptions.patchValue(previousStepValues.concat(results), { emitEvent: false });
          } else {
            this.selectedOptions.patchValue(results[0], { emitEvent: false });
          }
          this.selectedOptions.updateValueAndValidity();

          this.finishedInitialLoading.emit(true);
          this.initialLoading = false;
          this.cdr.detectChanges();
        });
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  validate(control: AbstractControl): ValidationErrors | null {
    return !this.selectedOptions.invalid ? null : { invalid: true };
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.selectedOptions.disable() : this.selectedOptions.enable();
    this.chipsDisabled = isDisabled;
  }

  ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();
  }

  loadMore(): void {
    if (this.hasMore) {
      this.doLookup(this.searchControl.value);
    }
  }

  doLookup(searchTerm: string) {
    this.loadingMore = true;
    this.dataService
      .doLookup(searchTerm, this.cursor, this.pageSize, this.extraData)
      .pipe(take(1))
      .subscribe(
        (resp) => {
          if (!this.cursor) {
            // After loading the first page if there are no results show the search not found message
            this.showSearchNotFound = resp.results.length === 0;
            this.options$$.next(resp.results);
          } else {
            const uniqueFieldIds = new Set();
            const concatOptions = this.options$$.value
              .concat(resp.results)
              .filter((option) => !uniqueFieldIds.has(option) && uniqueFieldIds.add(option));
            this.options$$.next(concatOptions);
          }
          this.cursor = resp.nextCursor;
          this.hasMore = resp.hasMore;
          // After initial loading (with no search) and there are no results show the warning
          if (!this.cursor && !searchTerm && resp.results.length === 0) {
            this.showNoResultsWarning = true;
          }
        },
        (err) => {
          console.error(err);
          this.alertService.openErrorSnack(this.translateService.instant('AUTOMATIONS.COMMON.ERROR_LOADING'));
        },
        () => {
          this.loadingMore = false;
          this.cdr.detectChanges();
        },
      );
  }

  displayFn(entity: any | string): Observable<string> {
    if (this.isEntityAPreviousStepValue(entity)) {
      return this.currentAutomationService.automation$.pipe(
        map((a) => {
          return convertToStepReferenceString(entity, a, this.translateService);
        }),
      );
    } else {
      return this.dataService.convertDataToDisplay(entity);
    }
  }

  isEntityAPreviousStepValue(entity: any | string): boolean {
    return typeof entity === 'string' && isReferencingAPreviousStep(entity);
  }

  removeEntity(index: number): void {
    if (!this.multiple) {
      this.selectedOptions.setValue(null);
    } else {
      this.selectedOptions.value.splice(index, 1);
      this.selectedOptions.setValue(this.selectedOptions.value); // trigger on changes
    }

    this.selectedOptions.markAsDirty();
  }

  selectEntity(event: MatAutocompleteSelectedEvent): void {
    this.entitySelected(event.option.value);
  }

  add(event: MatChipInputEvent): void {
    if (this.allowCustomValues) {
      this.entitySelected(event.value);
    }
  }

  replacementVariableSelected($event: string): void {
    this.entitySelected($event);
  }

  entitySelected(entity: any): void {
    if (this.multiple) {
      if (!this.selectedOptions.value.some((s) => isEqual(s, entity))) {
        this.selectedOptions.setValue(this.selectedOptions.value.concat(entity));
      }
    } else {
      this.selectedOptions.setValue(entity);
    }

    this.selectedOptions.markAsDirty();
    this.searchEntitiesInput.nativeElement.value = '';
    this.searchControl.setValue('');
  }

  inputClick(trigger: MatAutocompleteTrigger): void {
    if (!this.supportsSearching) {
      trigger.openPanel();
    }
  }
}
